import React, { useContext, useEffect } from "react"
import { useLocation, useHistory } from "react-router-dom"
import { useDispatch } from "react-redux"
import { usePost } from "../../hooks/usePost"
import { UserContext } from "../../components/HomeApp"
import Header from "../../components/shared/Header/Header"
import Heading from "../../components/shared/Heading"
import style from "../../components/shared/PageStyle.module.css"
import { PostListItem } from "../../components/posts/PostListItem"
import { Spinner } from "../../components/shared/Spinner"
import FreeTextFormat from "../../components/shared/FreeTextFormat"
import { isMobile } from "react-device-detect"
import UserCalendar from "../../components/home/components/UserCalendar"
import ActivityFeed from "../../components/home/components/ActivityFeed"
import ShowOfTheWeekSidebar from "../../components/home/components/ShowOfTheWeekSidebar"
import Footer from "../../components/shared/Footer/Footer"
import MakeStuff from "../../components/home/components/MakeStuff"
import { openAuthDialog } from "@components/Authentication"
import type { Post } from "../../types/posts"
import { ArrowLeftIcon } from "@mui/x-date-pickers/icons"
import { root_path } from "../../routes"
import { useReferralConversion } from "@hooks/useReferralConversion"
import { useReferralCapture } from "@hooks/useReferralCapture"

interface PostScreenParams {
  id: string
}

interface PostResponse {
  post: Post & { band_id: number }
}

const PostScreen: React.FC = () => {
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  // Parse post ID from pathname (e.g., "/p/123" -> "123")
  const postId = location.pathname.match(/^\/p\/(.+)/)?.[1] || ""
  const { user } = useContext(UserContext)

  // Preview link (?preview_token=...) grants access to a not-yet-announced
  // post — see Post#preview_token / PostsController#set_post!.
  const previewToken =
    new URLSearchParams(location.search).get("preview_token") || undefined

  // Capture referral when arriving via a tracked link
  useReferralCapture(postId || undefined)

  // Open auth dialog when URL has ?show-dialog (e.g. ?show-dialog=register)
  useEffect(() => {
    const initShowDialog = new URLSearchParams(window.location.search).get(
      "show-dialog",
    )
    if (initShowDialog) {
      dispatch(
        openAuthDialog({
          modalState: initShowDialog === "register" ? "register" : "auth",
          explanation:
            initShowDialog === "register"
              ? "Sign up to confirm your performance for the show"
              : null,
        }),
      )
    }
  }, [location.pathname, location.search, dispatch])

  const handleBack = () => {
    if (location.state?.from) {
      history.goBack()
    } else {
      history.push(root_path())
    }
  }

  const { status: postStatus, data: postData } = usePost(postId, undefined, {
    previewToken,
  })

  useReferralConversion({ convertibleType: "Post", convertibleId: postId })
  const post = (postData as PostResponse | undefined)?.post

  // Redirect song posts to parent album with song_id (mirrors Rails PostController behavior, no reload)
  const isSongWithParent =
    post &&
    postStatus === "success" &&
    ["song", "stand_alone_song", "link_song"].includes(post.post_type) &&
    Boolean(
      (post as { album_path?: string }).album_path ??
        (post as { parent_path?: string }).parent_path,
    )
  useEffect(() => {
    if (!isSongWithParent || !post) return
    const albumPath =
      (post as { album_path?: string }).album_path ??
      (post as { parent_path?: string }).parent_path
    if (albumPath) {
      const separator = albumPath.includes("?") ? "&" : "?"
      history.replace(`${albumPath}${separator}song_id=${post.id}`, location.state)
    }
  }, [isSongWithParent, post, history])

  // Redirect duplicate posts to the original
  const duplicatePostId = post && postStatus === "success"
    ? (post as { duplicate_post_id?: number }).duplicate_post_id
    : undefined
  useEffect(() => {
    if (duplicatePostId) {
      history.replace(`/p/${duplicatePostId}`)
    }
  }, [duplicatePostId, history])

  return (
    <div className={style.rootElStyle}>
      <Header user={user} />
      <div className="lg:grid lg:grid-cols-12 w-full pb-2 mt-4 px-3 lg:px-4 flex flex-col-reverse">
        <div className="lg:col-span-4 col-span-12 lg:pr-6">
          <MakeStuff />
          <ShowOfTheWeekSidebar className="mb-6" />
          {user && (
            <div className="mb-6">
              <UserCalendar user={user} />
            </div>
          )}
          {user && user.enable_activity_feed && !isMobile && (
            <div className="">
              <ActivityFeed />
            </div>
          )}
        </div>
        <div className="lg:col-span-8 col-span-12 lg:mt-0">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-sm focus:outline-none mb-2 text-bn-neon-red border border-bn-neon-red"
            style={{ borderRadius: "3px", padding: "2px 15px 2px 5px" }}
            aria-label="Go back"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back
          </button>
          {isSongWithParent || duplicatePostId ? (
            <div className="flex justify-center pt-16">
              <Spinner />
            </div>
          ) : postStatus === "success" ? (
            <ul>
              <PostListItem bandId={post.band_id} post={post} expanded={true} isPostPage={true} />
            </ul>
          ) : postStatus === "error" ? (
            <div>
              Sorry, we couldn't find that post. Try the search box above, a new
              post may have been created in its place.
            </div>
          ) : (
            <div className="flex justify-center pt-16">
              <Spinner />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default PostScreen
